/**
 * The chart itself: a frozen column of names, a two-row time axis, and one bar
 * per row positioned in percentages of the window.
 *
 * Percentages and not pixels, so the chart fills whatever width it is given and
 * needs no measuring pass - `ganttModel.ts` computes a `min-width` for the
 * canvas instead, and the frame scrolls sideways when the calendar does not fit.
 * A measured layout would have to re-run on every resize, on every splitter
 * drag and on the tab's first paint, and would be a frame behind each time.
 *
 * ── WHY THE NAMES ARE A SEPARATE COLUMN AND NOT A FIRST CELL ───────────────
 *
 * Because the names must stay put while the calendar scrolls. A single grid with
 * a sticky first column would work until the canvas got its `min-width`, at
 * which point the sticky cell scrolls with the rest. Two flex children - one
 * fixed, one scrolling - keep the two independent, and the rows line up because
 * both sides use the same fixed row height (`--crm-gantt-row`). That shared
 * height is load-bearing: change it in one place only.
 *
 * ── WHAT A BAR MEANS ──────────────────────────────────────────────────────
 *
 * A full bar is a plan - two dates somebody typed. A DIAMOND is a single date
 * with the other end missing, which is most of this CRM's data and must not be
 * drawn as a plan; a hollow bar is a phase whose dates were derived from its
 * tasks rather than stated. The tones say the rest: finished, late, or neither.
 * Anything the shape cannot say is in the `title`, which is where a mouse can
 * reach it - the chart is a scan, not a report.
 */
import { useMemo } from "react";
import { cn } from "@wa/ui";
import { fmtDate } from "../../../ui";
import {
  barGeometry,
  ganttAxis,
  ganttCanvasPx,
  offsetPct,
  startOfDay,
  type GanttRow,
  type GanttScale,
  type GanttWindow,
} from "./ganttModel";

/** Which shade a bar wears. Read off the row rather than passed in, so the two
 *  callers cannot disagree about what "late" looks like. */
function barTone(row: GanttRow): string {
  if (row.done) return "is-done";
  if (row.late) return "is-late";
  if (row.kind === "project") return "is-project";
  if (row.kind === "milestone") return "is-phase";
  return "is-task";
}

/**
 * The sentence a bar shows on hover.
 *
 * Built here rather than in the model because it is language, and the model has
 * no `t`. Both dates are printed even when one of them was assumed, with the
 * assumed side named - "due 12.06, no start date" is a fact the operator can act
 * on, while a bare "12.06 - 12.06" reads as a one-day task.
 */
function barTitle(row: GanttRow, words: GanttWords): string {
  const span = row.span;
  if (!span) return row.label;
  // `endMs` is exclusive - it is the start of the day after the last one - so
  // the date shown has to step back by a day or every bar reads as one day long
  // than it is.
  const lastDay = new Date(span.endMs - 1).toISOString();
  const start = new Date(span.startMs).toISOString();
  if (span.assumedStart) return `${row.label}\n${words.due}: ${fmtDate(lastDay)} (${words.noStart})`;
  if (span.assumedEnd) return `${row.label}\n${words.start}: ${fmtDate(start)} (${words.noDue})`;
  const range = `${fmtDate(start)} - ${fmtDate(lastDay)}`;
  return span.derived ? `${row.label}\n${range} (${words.derived})` : `${row.label}\n${range}`;
}

/** The words the chart needs. Passed in so the model and this file stay free of
 *  the i18n provider and can be rendered from a test or a story later. */
export interface GanttWords {
  start: string;
  due: string;
  noStart: string;
  noDue: string;
  derived: string;
  today: string;
  /** Read by a screen reader in place of the bars, which are decoration to it. */
  chartLabel: string;
}

export function GanttChart({
  rows,
  win,
  scale,
  nowMs,
  words,
}: {
  rows: readonly GanttRow[];
  win: GanttWindow;
  scale: GanttScale;
  /** The clock, passed in rather than read here, so the today line and the
   *  model's own "late" decision are made against the SAME instant. Two calls to
   *  `Date.now()` either side of midnight would draw a line that contradicts the
   *  colours. */
  nowMs: number;
  words: GanttWords;
}) {
  const axis = useMemo(() => ganttAxis(win, scale, nowMs), [win, scale, nowMs]);
  const canvasPx = useMemo(() => ganttCanvasPx(axis, scale), [axis, scale]);
  const todayPct = offsetPct(startOfDay(nowMs), win);

  return (
    <div className="crm-gantt">
      {/* The frozen half. Its head is an empty block of exactly the axis height,
          which is what keeps row 1 of the names beside row 1 of the bars. */}
      <div className="crm-gantt-names">
        <div className="crm-gantt-nhead" aria-hidden />
        {rows.map((row) => (
          <div
            key={row.id}
            className={cn("crm-gantt-name", `is-${row.kind}`, row.depth === 1 && "is-sub")}
            title={row.label}
          >
            <span className="crm-trunc">{row.label}</span>
          </div>
        ))}
      </div>

      {/* The scrolling half. `crm-gantt-scroll` is the only thing that scrolls -
          putting the overflow on the card would make a sideways swipe anywhere
          inside it drag the chart, which is the trap `.crm-list-scroll` exists
          for elsewhere in this app. */}
      <div className="crm-gantt-scroll">
        <div className="crm-gantt-canvas" style={{ minWidth: canvasPx }} role="img" aria-label={words.chartLabel}>
          <div className="crm-gantt-axis">
            <div className="crm-gantt-major">
              {axis.major.map((tick) => (
                <span
                  key={tick.key}
                  className={cn("crm-gantt-tick", tick.current && "is-now")}
                  style={{
                    left: `${offsetPct(tick.startMs, win)}%`,
                    width: `${offsetPct(tick.endMs, win) - offsetPct(tick.startMs, win)}%`,
                  }}
                >
                  <span className="crm-trunc">{tick.label}</span>
                </span>
              ))}
            </div>
            <div className="crm-gantt-minor">
              {axis.minor.map((tick) => (
                <span
                  key={tick.key}
                  className={cn("crm-gantt-tick", tick.current && "is-now")}
                  style={{
                    left: `${offsetPct(tick.startMs, win)}%`,
                    width: `${offsetPct(tick.endMs, win) - offsetPct(tick.startMs, win)}%`,
                  }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div className="crm-gantt-body">
            {/* The gridlines are drawn from the MINOR ticks rather than as a
                repeating background, because the columns are not equal widths:
                a month is 28 to 31 days and a repeating gradient would drift out
                of step with the labels above it within one screen. */}
            <div className="crm-gantt-grid" aria-hidden>
              {axis.minor.map((tick) => (
                <span key={tick.key} style={{ left: `${offsetPct(tick.startMs, win)}%` }} />
              ))}
            </div>

            {/* Today, over the grid and under the bars. It is drawn even when the
                project is finished - see `ganttWindow`, which keeps today inside
                the window precisely so this line always has somewhere to be. */}
            <span className="crm-gantt-today" style={{ left: `${todayPct}%` }} title={words.today} aria-hidden />

            {rows.map((row) => {
              const span = row.span;
              const geom = span ? barGeometry(span, win) : null;
              // A marker rather than a bar when one end was never typed. The
              // width is still set, so a single-day marker on a day scale is a
              // real day wide and the same marker on a year scale collapses to
              // the stylesheet's minimum - both correct.
              const marker = !!span && (span.assumedStart || span.assumedEnd);
              return (
                <div key={row.id} className={cn("crm-gantt-row", row.depth === 1 && "is-sub")}>
                  {geom ? (
                    <span
                      className={cn("crm-gantt-bar", barTone(row), marker && "is-mark", span?.derived && "is-derived")}
                      style={{ left: `${geom.leftPct}%`, width: `${geom.widthPct}%` }}
                      title={barTitle(row, words)}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
